import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Card } from './ui/Card';
import { Pill } from './ui/Pill';
import { formatAmount } from '../lib/format';
import { severityTone, type Signal } from '../hooks/useSignals';

/** Render a signal's impact as a localized chip label (¥ / days / count). */
function useImpactLabel() {
  const { t, i18n } = useTranslation();
  return (s: Signal): string | null => {
    if (s.impactUnit === 'JPY') {
      if (!s.impactValue) return null;
      return t('signals.impact.amount', { amount: formatAmount(s.impactValue, 'JPY', i18n.language) });
    }
    if (s.impactUnit === 'days') return t('signals.impact.days', { days: s.impactValue });
    return t('signals.impact.count', { count: s.impactValue });
  };
}

function SeverityPill({ severity }: { severity: Signal['severity'] }) {
  const { t } = useTranslation();
  return <Pill tone={severityTone(severity)}>{t(`signals.severity.${severity}`)}</Pill>;
}

/**
 * The hero "do this next" card — the top-ranked signal, rendered large with the
 * agent that raised it, the translated rationale, the impact, and a prominent
 * one-click CTA that deep-links into the owning page.
 */
export function NextBestActionHero({ signal }: { signal: Signal }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const impactLabel = useImpactLabel();
  const impact = impactLabel(signal);

  return (
    <Card className="p-6 bg-gradient-to-br from-accent-50 to-white dark:from-accent-900/30 dark:to-ink-900 border-accent-200 dark:border-accent-800">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-accent-700 dark:text-accent-200">
          <Sparkles size={13} />
          {t('home.doThisNext')}
        </span>
        <SeverityPill severity={signal.severity} />
        <Pill tone="neutral">{signal.agent}</Pill>
        {impact && <Pill tone="muted">{impact}</Pill>}
      </div>
      <h2 className="display text-xl font-semibold mb-1.5">{t(signal.titleKey, signal.params)}</h2>
      <p className="text-sm text-ink-600 dark:text-ink-300 max-w-2xl mb-4">
        {t(signal.rationaleKey, signal.params)}
      </p>
      <button
        type="button"
        onClick={() => navigate(signal.ctaPath)}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-accent-600 text-white text-sm font-medium hover:bg-accent-700 transition-colors"
      >
        {t(signal.ctaKey)}
        <ArrowRight size={15} />
      </button>
    </Card>
  );
}

/** A compact signal row used in the feed list (Home below-the-hero + Inbox). */
export function SignalRow({ signal, compact = false }: { signal: Signal; compact?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const impactLabel = useImpactLabel();
  const impact = impactLabel(signal);

  return (
    <div className="px-5 py-4 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <SeverityPill severity={signal.severity} />
          <span className="text-[11px] text-ink-500 dark:text-ink-400">{signal.agent}</span>
          {impact && <Pill tone="muted">{impact}</Pill>}
        </div>
        <div className="font-medium truncate">{t(signal.titleKey, signal.params)}</div>
        {!compact && (
          <p className="text-xs text-ink-500 dark:text-ink-400 mt-0.5 line-clamp-2 max-w-2xl">
            {t(signal.rationaleKey, signal.params)}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => navigate(signal.ctaPath)}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-accent-300 dark:border-accent-700 text-accent-700 dark:text-accent-200 text-xs font-medium hover:bg-accent-50 dark:hover:bg-accent-900/30 transition-colors"
      >
        {t(signal.ctaKey)}
        <ArrowRight size={13} />
      </button>
    </div>
  );
}
